/**
 * v3.23 通知中心：应用内通知列表（LS `mesh_notices` 持久化，上限 100 条）。
 *
 * 与系统通知（lib/notify）互补：系统通知负责即时唤醒，本中心负责已读/历史收口。
 * 通知类型：chat（私聊）/ group（群聊）/ circle（朋友圈动态）/ system（系统）。
 */
import { create } from 'zustand';

export type NoticeType = 'chat' | 'group' | 'circle' | 'system';

export interface Notice {
  id: string;
  type: NoticeType;
  title: string;
  body: string;
  ts: number;
  read: boolean;
}

const NOTICES_KEY = 'mesh_notices';
const MAX_NOTICES = 100;

function load(): Notice[] {
  try {
    const raw = window.localStorage.getItem(NOTICES_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as Notice[];
    return Array.isArray(list) ? list.slice(0, MAX_NOTICES) : [];
  } catch {
    return [];
  }
}

function persist(list: Notice[]): void {
  try {
    window.localStorage.setItem(NOTICES_KEY, JSON.stringify(list.slice(0, MAX_NOTICES)));
  } catch {
    /* 存储不可用忽略 */
  }
}

export interface NoticeState {
  notices: Notice[];
  addNotice(type: NoticeType, title: string, body: string): void;
  markAllRead(): void;
  clearNotices(): void;
  unreadCount(): number;
}

export const useNoticeStore = create<NoticeState>((set, get) => ({
  notices: load(),

  addNotice: (type, title, body) => {
    const item: Notice = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type,
      title,
      body: body.length > 200 ? `${body.slice(0, 200)}…` : body,
      ts: Date.now(),
      read: false,
    };
    const next = [item, ...get().notices].slice(0, MAX_NOTICES);
    set({ notices: next });
    persist(next);
  },

  markAllRead: () => {
    const next = get().notices.map((n) => ({ ...n, read: true }));
    set({ notices: next });
    persist(next);
  },

  clearNotices: () => {
    set({ notices: [] });
    persist([]);
  },

  unreadCount: () => get().notices.filter((n) => !n.read).length,
}));

export default useNoticeStore;
